import { useEffect, useState } from 'react'

import { Tooltip } from '../Tooltip'

const BUTTON_SELECTOR = '.ck.ck-balloon-panel.ck-toolbar-container .ck-button'

function getButtonTooltip(button: HTMLElement): string {
    return (
        button.getAttribute('data-cke-tooltip-text')?.trim() ||
        button.getAttribute('aria-label')?.trim() ||
        ''
    )
}

function isVisibleBalloonButton(button: HTMLElement): boolean {
    if (!button.isConnected) return false

    const panel = button.closest('.ck.ck-balloon-panel.ck-toolbar-container')
    if (!panel?.classList.contains('ck-balloon-panel_visible')) return false

    const rect = button.getBoundingClientRect()
    return rect.width > 0 && rect.height > 0
}

export function BalloonToolbarTooltip() {
    const [target, setTarget] = useState<HTMLElement | null>(null)
    const [content, setContent] = useState('')

    const hide = () => {
        setTarget(null)
        setContent('')
    }

    useEffect(() => {
        const handlePointerOver = (event: PointerEvent) => {
            const button = (event.target as HTMLElement | null)?.closest(BUTTON_SELECTOR)
            if (!(button instanceof HTMLElement) || !isVisibleBalloonButton(button)) return

            const tooltip = getButtonTooltip(button)
            if (!tooltip) return

            setTarget(button)
            setContent(tooltip)
        }

        const handlePointerOut = (event: PointerEvent) => {
            const button = (event.target as HTMLElement | null)?.closest(BUTTON_SELECTOR)
            if (!(button instanceof HTMLElement)) {
                setTarget((current) => (current && !isVisibleBalloonButton(current) ? null : current))
                return
            }

            const next = event.relatedTarget as HTMLElement | null
            if (next?.closest(BUTTON_SELECTOR) === button) return

            setTarget((current) => (current === button ? null : current))
        }

        // Clicking a toolbar button (e.g. Link) hides the balloon before pointerout
        // can run. Clear immediately so the tooltip does not follow the hidden
        // panel to (0, 0).
        const handlePointerDown = (event: PointerEvent) => {
            const button = (event.target as HTMLElement | null)?.closest(BUTTON_SELECTOR)
            if (button instanceof HTMLElement) hide()
        }

        document.addEventListener('pointerover', handlePointerOver)
        document.addEventListener('pointerout', handlePointerOut)
        document.addEventListener('pointerdown', handlePointerDown, true)

        return () => {
            document.removeEventListener('pointerover', handlePointerOver)
            document.removeEventListener('pointerout', handlePointerOut)
            document.removeEventListener('pointerdown', handlePointerDown, true)
        }
    }, [])

    useEffect(() => {
        if (!target) return

        const dismissIfHidden = () => {
            if (!isVisibleBalloonButton(target)) hide()
        }

        const root = target.closest('.ck-body-wrapper') ?? document.body
        const observer = new MutationObserver(dismissIfHidden)
        observer.observe(root, {
            subtree: true,
            childList: true,
            attributes: true,
            attributeFilter: ['class', 'style'],
        })

        dismissIfHidden()

        return () => observer.disconnect()
    }, [target])

    if (!target || !content) return null

    return (
        <Tooltip
            content={content}
            placement="top"
            open
            openDelay={0}
            closeDelay={0}
            anchor={target}
        />
    )
}
